import { useState, useEffect, useCallback } from 'react';
import { campaignsAPI, agentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import UpgradeBanner from '../components/UpgradeBanner';
import {
  Megaphone, Plus, Play, Pause, Trash2, Loader2,
  Users, PhoneCall, Clock, Calendar, RotateCcw, Upload, FileSpreadsheet,
  Eye, Phone, AlertCircle, CheckCircle2, Timer, Zap
} from 'lucide-react';
import { toast } from 'sonner';

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [csvInfo, setCsvInfo] = useState(null);
  const [detailCampaign, setDetailCampaign] = useState(null); // campaign ID for detail view
  const [detailData, setDetailData] = useState(null); // detail API response
  const [detailLoading, setDetailLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', agent_id: '', contacts: '',
    retry_attempts: 2, retry_interval: 30, schedule_time: ''
  });

  useEffect(() => {
    Promise.all([campaignsAPI.list(), agentsAPI.list()])
      .then(([cRes, aRes]) => { setCampaigns(cRes.data); setAgents(aRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Auto-refresh when any campaign is in "preparing" or "running" state
  useEffect(() => {
    const hasActive = campaigns.some(c => c.status === 'preparing' || c.status === 'running');
    if (!hasActive) return;
    const interval = setInterval(() => { loadCampaigns(); }, 5000);
    return () => clearInterval(interval);
  }, [campaigns]);

  const loadCampaigns = async () => {
    const res = await campaignsAPI.list();
    setCampaigns(res.data);
  };

  // ── Campaign details loading ──
  const loadDetails = useCallback(async (campaignId) => {
    try {
      setDetailLoading(true);
      const res = await campaignsAPI.details(campaignId);
      setDetailData(res.data);
    } catch (err) {
      console.error('Failed to load campaign details:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Auto-refresh details when detail modal is open and campaign is active
  useEffect(() => {
    if (!detailCampaign) return;
    loadDetails(detailCampaign);
    const campaign = campaigns.find(c => c.id === detailCampaign);
    const isActive = campaign && ['running', 'preparing'].includes(campaign.status);
    if (!isActive) return;
    const interval = setInterval(() => loadDetails(detailCampaign), 3000);
    return () => clearInterval(interval);
  }, [detailCampaign, campaigns, loadDetails]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.agent_id) {
      toast.error('Name and agent are required');
      return;
    }
    setSaving(true);
    try {
      const contacts = form.contacts.split('\n').filter(Boolean).map(line => {
        const parts = line.split(',').map(s => s.trim());
        return { name: parts[0] || '', phone: parts[1] || parts[0] };
      });
      await campaignsAPI.create({
        name: form.name, description: form.description, agent_id: form.agent_id,
        contacts, retry_attempts: form.retry_attempts, retry_interval: form.retry_interval,
        schedule_time: form.schedule_time || null
      });
      toast.success('Campaign created');
      setShowCreate(false);
      setCsvInfo(null);
      setForm({ name: '', description: '', agent_id: '', contacts: '', retry_attempts: 2, retry_interval: 30, schedule_time: '' });
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    } finally { setSaving(false); }
  };

  const handleStart = async (id) => {
    try { await campaignsAPI.start(id); toast.success('Preparing calls...'); loadCampaigns(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to start campaign'); }
  };

  const handlePause = async (id) => {
    try { await campaignsAPI.pause(id); toast.success('Campaign paused'); loadCampaigns(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to pause'); }
  };

  const handleRerun = async (id) => {
    try { await campaignsAPI.rerun(id); toast.success('Campaign restarted'); loadCampaigns(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to rerun campaign'); }
  };

  const handleDelete = async (id) => {
    try { await campaignsAPI.delete(id); toast.success('Campaign deleted'); loadCampaigns(); }
    catch { toast.error('Failed to delete'); }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-800 text-slate-400 border-slate-700',
      preparing: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
      running: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
      paused: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
      completed: 'bg-blue-600/20 text-blue-400 border-blue-600/30'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 max-w-7xl" data-testid="campaigns-page">
      {/* Upgrade Banner for Standard Plan Users */}
      <UpgradeBanner />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-white">Campaigns</h1>
          <p className="text-slate-400 mt-1">Manage outbound call campaigns</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-campaign-button"
          className="bg-amber-600 hover:bg-amber-700 text-white shadow-[0_0_20px_rgba(245,158,11,0.3)]">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {campaigns.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="no-campaigns">
          <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold font-['Outfit'] text-white mb-2">No campaigns yet</h3>
          <p className="text-slate-400 mb-6">Create your first outbound campaign</p>
          <Button onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Create Campaign
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign, i) => {
            const progress = campaign.total_contacts > 0
              ? Math.round((campaign.completed_calls / campaign.total_contacts) * 100) : 0;
            const agentName = agents.find(a => a.id === campaign.agent_id)?.name || 'Unknown Agent';

            return (
              <div key={campaign.id} className="glass-card rounded-xl p-5 animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`campaign-card-${campaign.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold font-['Outfit'] text-lg">{campaign.name}</h3>
                      <Badge className={getStatusColor(campaign.status)} data-testid={`campaign-status-${campaign.id}`}>
                        {campaign.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{campaign.description || 'No description'}</p>

                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {campaign.total_contacts} contacts</span>
                      <span className="flex items-center gap-1"><PhoneCall className="w-3 h-3" /> {campaign.completed_calls} completed</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Retry: {campaign.retry_attempts}x</span>
                      <span className="flex items-center gap-1">Agent: {agentName}</span>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                        <span>Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-1.5 bg-slate-800" />
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button size="sm" onClick={() => setDetailCampaign(campaign.id)}
                      className="bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 border border-slate-600/30"
                      data-testid={`details-campaign-${campaign.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Details
                    </Button>
                    {campaign.status === 'draft' || campaign.status === 'paused' ? (
                      <Button size="sm" onClick={() => handleStart(campaign.id)} className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-600/30"
                        data-testid={`start-campaign-${campaign.id}`}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Start
                      </Button>
                    ) : campaign.status === 'preparing' ? (
                      <Button size="sm" disabled className="bg-purple-600/20 text-purple-400 border border-purple-600/30 cursor-wait"
                        data-testid={`preparing-campaign-${campaign.id}`}>
                        <svg className="w-3.5 h-3.5 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
                        Preparing...
                      </Button>
                    ) : campaign.status === 'running' ? (
                      <Button size="sm" onClick={() => handlePause(campaign.id)} className="bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 border border-amber-600/30"
                        data-testid={`pause-campaign-${campaign.id}`}>
                        <Pause className="w-3.5 h-3.5 mr-1" /> Pause
                      </Button>
                    ) : campaign.status === 'completed' || campaign.status === 'failed' ? (
                      <Button size="sm" onClick={() => handleRerun(campaign.id)} className="bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 border border-amber-600/30"
                        data-testid={`rerun-campaign-${campaign.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rerun
                      </Button>
                    ) : null}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(campaign.id)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-campaign-${campaign.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg bg-[#0B1221] border-white/10 text-white" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">Create Campaign</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Campaign Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Q1 Sales Outreach"
                data-testid="campaign-name-input" className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Campaign description..."
                data-testid="campaign-desc-input" className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">AI Agent *</Label>
              <Select value={form.agent_id} onValueChange={(v) => setForm(p => ({ ...p, agent_id: v }))}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white" data-testid="campaign-agent-select">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {agents.map(a => <SelectItem key={a.id} value={a.id} className="text-white hover:bg-white/5">{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-2 block">Contacts</Label>
              <div className="flex items-center gap-2 mb-2">
                <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg bg-amber-600/10 border border-amber-600/30 text-amber-300 text-sm hover:bg-amber-600/20 transition" data-testid="csv-upload-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Parsing...' : 'Upload CSV'}
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    data-testid="csv-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploading(true);
                      setCsvInfo(null);
                      try {
                        const res = await campaignsAPI.uploadContacts(file);
                        const { contacts, total, skipped } = res.data;
                        const lines = contacts.map(c => `${c.name}, ${c.phone}`).join('\n');
                        setForm(p => ({ ...p, contacts: lines }));
                        setCsvInfo({ total, skipped });
                        toast.success(`Loaded ${total} contacts from CSV${skipped ? ` (${skipped} skipped)` : ''}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to parse CSV');
                      }
                      setUploading(false);
                      e.target.value = '';
                    }}
                  />
                </label>
                {csvInfo && (
                  <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="csv-info">
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-400" />
                    {csvInfo.total} contacts loaded{csvInfo.skipped > 0 && `, ${csvInfo.skipped} skipped`}
                  </span>
                )}
              </div>
              <Textarea value={form.contacts} onChange={(e) => setForm(p => ({ ...p, contacts: e.target.value }))}
                placeholder={"John Smith, +12025551234\nJane Doe, +12025555678\n\nOr upload a CSV file above"} rows={4}
                data-testid="campaign-contacts-input" className="bg-slate-950 border-white/10 text-white placeholder:text-slate-600 font-mono text-sm" />
              <p className="text-xs text-slate-500 mt-1">One contact per line: Name, Phone. Or upload a CSV with Name and Phone columns.</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Retry Attempts</Label>
                <Input type="number" value={form.retry_attempts} onChange={(e) => setForm(p => ({ ...p, retry_attempts: parseInt(e.target.value) || 0 }))}
                  data-testid="campaign-retry-input" className="bg-slate-950 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Retry Delay (sec)</Label>
                <Input type="number" value={form.retry_interval} onChange={(e) => setForm(p => ({ ...p, retry_interval: parseInt(e.target.value) || 30 }))}
                  data-testid="campaign-retry-interval-input" className="bg-slate-950 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-slate-300 text-sm mb-2 block">Schedule</Label>
                <Input type="datetime-local" value={form.schedule_time} onChange={(e) => setForm(p => ({ ...p, schedule_time: e.target.value }))}
                  data-testid="campaign-schedule-input" className="bg-slate-950 border-white/10 text-white" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-campaign-button"
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Campaign
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Details Modal */}
      <Dialog open={!!detailCampaign} onOpenChange={(open) => { if (!open) { setDetailCampaign(null); setDetailData(null); } }}>
        <DialogContent className="max-w-2xl bg-[#0B1221] border-white/10 text-white max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit'] flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-amber-500" />
              Campaign Call Details
            </DialogTitle>
          </DialogHeader>

          {detailLoading && !detailData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              <span className="ml-2 text-slate-400">Loading details...</span>
            </div>
          ) : detailData ? (
            <div className="space-y-4 overflow-y-auto flex-1 pr-1">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                  { label: 'Active', count: detailData.summary?.active || 0, bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', icon: Zap },
                  { label: 'Preparing', count: detailData.summary?.preparing || 0, bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-400', icon: Timer },
                  { label: 'Queued', count: detailData.summary?.queued || 0, bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', icon: Clock },
                  { label: 'Completed', count: detailData.summary?.completed || 0, bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-400', icon: CheckCircle2 },
                  { label: 'Failed', count: detailData.summary?.failed || 0, bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', icon: AlertCircle },
                  { label: 'Cancelled', count: detailData.summary?.cancelled || 0, bg: 'bg-slate-700/50', border: 'border-slate-600/20', text: 'text-slate-400', icon: Pause },
                ].map(s => (
                  <div key={s.label} className={`${s.bg} border ${s.border} rounded-lg p-2 text-center`}>
                    <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.text}`} />
                    <div className={`text-lg font-bold ${s.text}`}>{s.count}</div>
                    <div className="text-[10px] text-slate-500">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Global Queue Info */}
              {detailData.global_queue && (
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Global Call Queue</span>
                  <div className="flex items-center gap-4">
                    <span className="text-amber-400">{detailData.global_queue.active_calls}/{detailData.global_queue.max_concurrent} slots used</span>
                    <span className="text-blue-400">{detailData.global_queue.total_queued} waiting</span>
                  </div>
                </div>
              )}

              {/* Contact List */}
              <div className="space-y-1.5">
                {(detailData.contacts || []).map((contact, idx) => {
                  const statusConfig = {
                    active: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: Phone, label: 'Live', pulse: true },
                    preparing: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', icon: Timer, label: 'Preparing', pulse: true },
                    queued: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', icon: Clock, label: 'In Queue', pulse: false },
                    completed: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', icon: CheckCircle2, label: 'Completed', pulse: false },
                    failed: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertCircle, label: 'Failed', pulse: false },
                    cancelled: { bg: 'bg-slate-700/50', border: 'border-slate-600/30', text: 'text-slate-400', icon: Pause, label: 'Cancelled', pulse: false },
                  };
                  const sc = statusConfig[contact.status] || statusConfig.queued;
                  const Icon = sc.icon;
                  return (
                    <div key={idx} className={`${sc.bg} border ${sc.border} rounded-lg px-3 py-2.5 flex items-center gap-3`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${sc.bg}`}>
                        <Icon className={`w-4 h-4 ${sc.text} ${sc.pulse ? 'animate-pulse' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white truncate">{contact.name || 'Unknown'}</span>
                          <span className="text-xs text-slate-500">{contact.phone}</span>
                        </div>
                        {contact.duration > 0 && (
                          <span className="text-[10px] text-slate-500">{contact.duration}s duration</span>
                        )}
                        {contact.error && (
                          <span className="text-[10px] text-red-400">{contact.error}</span>
                        )}
                      </div>
                      <Badge className={`${sc.bg} ${sc.text} border ${sc.border} text-[10px]`}>
                        {sc.pulse && <span className="w-1.5 h-1.5 rounded-full bg-current mr-1 animate-pulse inline-block" />}
                        {sc.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">No details available</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
